// Darstellungsregeln für die Personal-Seiten im Widget-Stil (design_handoff_personal). Reine Funktionen —
// fachlich entschieden wird weiterhin in lib/zeiterfassung/* und lib/personal/*; hier nur, wie es aussieht.
import { anzurechnendePause } from '../zeiterfassung/berechnung';

export type Ton = 'ok' | 'warn' | 'neutral';

/** Ganzzahliger Anteil in Prozent, auf 0…100 begrenzt (Balken „85% vom Soll“). Ohne Bezugsgröße 0. */
export function anteilProzent(wert: number, bezug: number): number {
  if (!bezug || !Number.isFinite(wert) || !Number.isFinite(bezug)) return 0;
  return Math.max(0, Math.min(100, Math.round((wert / bezug) * 100)));
}

/** Farbe eines Monats-/Kontosaldos in den Kennzahlen: ab 0 grün (Überstunden), darunter amber — wie bisher die Kachel. */
export const saldoTonKennzahl = (minuten: number): Exclude<Ton, 'neutral'> => (minuten >= 0 ? 'ok' : 'warn');

/** Farbe des Saldo-Chips je Tag: 0 neutral, plus grün, minus amber — wie bisher die Tageszeile. */
export const saldoTonTag = (minuten: number): Ton => (minuten > 0 ? 'ok' : minuten < 0 ? 'warn' : 'neutral');

/** Unterzeile der Saldo-Kennzahl. */
export const saldoUnter = (minuten: number): string => (minuten >= 0 ? 'Überstunden' : 'Minusstunden');

/** Balken der Saldo-Kennzahl: Betrag relativ zu einem Viertel des Solls (Handoff-Prototyp). */
export const saldoBalken = (saldo: number, soll: number): number => anteilProzent(Math.abs(saldo), Math.max(1, soll) / 4);

export const TON_FARBE: Record<Ton, { farbe: string; flaeche: string }> = {
  ok: { farbe: 'var(--ok)', flaeche: 'var(--ok-soft)' },
  warn: { farbe: 'var(--warn)', flaeche: 'var(--warn-soft)' },
  neutral: { farbe: 'var(--ink3)', flaeche: 'var(--fill2)' },
};

/** Anteil des Urlaubsrings „Rest von Anspruch“ als 0…1. */
export function urlaubsRingAnteil(rest: number, anspruch: number): number {
  if (!anspruch || anspruch <= 0 || !Number.isFinite(rest)) return 0;
  return Math.max(0, Math.min(1, rest / anspruch));
}

/** Tag-Abzeichen der Tageszeile: Wochentag kurz in Großbuchstaben + Tageszahl. */
export function tagAbzeichen(tag: string): { wochentag: string; zahl: string } {
  const d = new Date(`${tag}T12:00:00`);
  return { wochentag: d.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '').toUpperCase(), zahl: String(d.getDate()) };
}

/** Status-Text und Ton der Zeit-Anträge (Korrektur/Mehrarbeit) — Texte wie bisher. */
export function antragStatus(status: string): { label: string; ton: 'ok' | 'warn' | 'bad' | 'neutral' } {
  switch (status) {
    case 'offen': return { label: 'Offen', ton: 'warn' };
    case 'genehmigt': return { label: 'Genehmigt', ton: 'ok' };
    case 'abgelehnt': return { label: 'Abgelehnt', ton: 'bad' };
    default: return { label: 'Zurückgezogen', ton: 'neutral' };
  }
}

/** Art einer Zeit-Korrektur — Texte wie bisher. */
export const korrekturArt = (art: string): string => (art === 'neu' ? 'Nachtrag' : art === 'aendern' ? 'Änderung' : 'Löschung');

/**
 * Live-Anzeige „Arbeitszeit netto“ im Zeit-Dialog: Zeitraum minus Pause, wobei — wie in der
 * Berechnung — ab sechs Stunden mindestens die gesetzliche Pause abgeht. Ungültige Eingaben → null.
 */
export function nettoVorschau(tag: string, von: string, bis: string, pause: string): number | null {
  const beginn = new Date(`${tag}T${von}:00`).getTime();
  const ende = new Date(`${tag}T${bis}:00`).getTime();
  if (Number.isNaN(beginn) || Number.isNaN(ende) || ende <= beginn) return null;
  const brutto = Math.round((ende - beginn) / 60000);
  const pauseMin = Math.max(0, Number(pause) || 0);
  if (pauseMin >= brutto) return null;
  return brutto - anzurechnendePause(brutto, pauseMin);
}

// ─── Ersatz für die bisherigen window.prompt-Abläufe (gleiche Prüfungen) ──────────

export type MehrarbeitPruefung =
  | { ok: true; datum: string; minuten: number; grund: string }
  | { ok: false };

/**
 * Mehrarbeit beantragen: bisher drei prompt-Fragen. Leerer Tag, leere Stundenangabe oder leerer
 * Grund brachen still ab — der Dialog lässt dann nicht absenden. Die Minuten werden wie bisher
 * aus „2“ oder „1,5“ berechnet; ob die Zahl sinnvoll ist, prüft weiterhin mehrarbeitBeantragen.
 */
export function pruefeMehrarbeit(tag: string, stunden: string, grund: string): MehrarbeitPruefung {
  if (!tag || !stunden || !grund.trim()) return { ok: false };
  return { ok: true, datum: tag, minuten: Math.round(Number(stunden.replace(',', '.')) * 60), grund };
}

export type AuszahlungPruefung =
  | { ok: true; minuten: number; ueberGuthaben: boolean }
  | { ok: false; fehler: string | null };

/**
 * Überstunden auszahlen: bisher prompt + ggf. confirm. Leere Eingabe brach still ab (fehler null),
 * eine ungültige oder nicht positive Zahl meldete „Bitte eine Stundenzahl angeben“. Mehr als das
 * Guthaben braucht eine zweite Bestätigung (ueberGuthaben).
 */
export function pruefeAuszahlung(eingabe: string, kontoMinuten: number): AuszahlungPruefung {
  if (!eingabe) return { ok: false, fehler: null };
  const minuten = Math.round(Number(eingabe.replace(',', '.')) * 60);
  if (!Number.isFinite(minuten) || minuten <= 0) return { ok: false, fehler: 'Bitte eine Stundenzahl angeben' };
  return { ok: true, minuten, ueberGuthaben: minuten > kontoMinuten };
}

/** Vorschlag im Auszahl-Dialog: volle Stunden des Guthabens (wie bisher der prompt-Vorgabewert). */
export const auszahlVorschlag = (kontoMinuten: number): string => Math.floor(kontoMinuten / 60).toString();

/** Eintrag im Verlauf eines Abwesenheitsantrags — Texte wie bisher, Farbe je Aktion (Handoff §4). */
export function verlaufAktion(aktion: string, stufe?: number | null): { label: string; farbe: string } {
  switch (aktion) {
    case 'gestellt': return { label: 'Gestellt', farbe: 'var(--info)' };
    case 'kommentar': return { label: 'Kommentar', farbe: 'var(--ink2)' };
    case 'genehmigt': return { label: `Genehmigt${stufe ? ` (Stufe ${stufe})` : ''}`, farbe: 'var(--ok)' };
    case 'abgelehnt': return { label: 'Abgelehnt', farbe: 'var(--bad)' };
    default: return { label: 'Storniert', farbe: 'var(--ink3)' };
  }
}

/**
 * Ampel-Chip eines Nachweises (Stufe aus lib/personal/akte `ampel`) — Texte wie bisher:
 * überfällig seit …, fällig am …, nächste …, ohne Termin.
 */
export function ampelChip(stufe: 'ok' | 'bald' | 'faellig' | 'unbekannt', datumText: string): { text: string; farbe: string; flaeche: string } {
  switch (stufe) {
    case 'faellig': return { text: `überfällig seit ${datumText}`, farbe: 'var(--bad)', flaeche: 'var(--bad-soft)' };
    case 'bald': return { text: `fällig am ${datumText}`, farbe: 'var(--warn)', flaeche: 'var(--warn-soft)' };
    case 'ok': return { text: `nächste ${datumText}`, farbe: 'var(--ok)', flaeche: 'var(--ok-soft)' };
    default: return { text: 'ohne Termin', farbe: 'var(--ink2)', flaeche: 'var(--fill2)' };
  }
}

/** Farbe des „gültig bis"-Chips eines Dokuments — dieselbe Ampel ohne Text. */
export const ampelFarbe = (stufe: 'ok' | 'bald' | 'faellig' | 'unbekannt') => {
  const { farbe, flaeche } = ampelChip(stufe, '');
  return { farbe, flaeche };
};

/** Zweibuchstabige Initialen für den Avatar der Akte. */
export const initialen = (name: string): string =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((t) => t[0]).join('').toUpperCase();

// ─── Typ- und Statusfarben (Farbnamen aus TYP_INFO/STATUS_INFO → Tokens) ─────────

/** Farbname aus TYP_INFO bzw. STATUS_INFO (emerald, red, sky, violet, amber, gray) als Token-Paar. */
export function farbeAusName(name: string | undefined): { farbe: string; flaeche: string } {
  switch (name) {
    case 'emerald': return { farbe: 'var(--ok)', flaeche: 'var(--ok-soft)' };
    case 'red': return { farbe: 'var(--bad)', flaeche: 'var(--bad-soft)' };
    case 'sky': return { farbe: 'var(--info)', flaeche: 'var(--info-soft)' };
    case 'violet': return { farbe: 'var(--pur)', flaeche: 'var(--pur-soft)' };
    case 'amber': return { farbe: 'var(--warn)', flaeche: 'var(--warn-soft)' };
    default: return { farbe: 'var(--ink2)', flaeche: 'var(--fill2)' };
  }
}
